import gsap from "gsap";
import {createFocusTrap} from "focus-trap";
import {module as Module} from "modujs";
import {$html} from "../utils/dom";
import {delegateEvent} from "../utils/events.js";

export default class Gallery extends Module {
    static ACTIVE_CLASS = 'has-gallery-open';

    constructor(m) {
        super(m);

        /******************/
        /* Binding */
        /******************/

        /* Primary level */
        this.onPrimaryActivateBind = this.onPrimaryActivate.bind(this);
        this.onPrimaryPostActivateBind = this.onPrimaryPostActivate.bind(this);
        this.onPrimaryDeactivateBind = this.onPrimaryDeactivate.bind(this);
        this.onPrimaryPostDeactivateBind = this.onPrimaryPostDeactivate.bind(this);

        /* Secondary level */
        this.onSecondaryActivateBind = this.onSecondaryActivate.bind(this);
        this.onSecondaryPostActivateBind = this.onSecondaryPostActivate.bind(this);
        this.onSecondaryDeactivateBind = this.onSecondaryDeactivate.bind(this);
        this.onSecondaryPostDeactivateBind = this.onSecondaryPostDeactivate.bind(this);

        this.openGalleryBind = this.openGallery.bind(this);
        this.closeGalleryBind = this.closeGallery.bind(this);

        this.openSecondaryBind = this.openSecondary.bind(this);
        this.closeSecondaryBind = this.closeSecondary.bind(this);

        /******************/
        /* UI elements */
        /******************/
        this.$header = this.$("header")[0];
        this.$close = this.$("close")[0];
        this.$primaryPanel = this.$("primary")[0];
        this.$secondaryPanel = this.$("secondary")[0];
        this.$secondaryButtons = this.$("secondaryButton");
        this.$secondaryClose = this.$("secondaryClose")[0];

        /******************/
        /* State */
        /******************/
        this.isPrimaryOpen = false;
        this.currentSecondaryFocusTrap = null;

        /******************/
        /* Focus trap */
        /******************/

        /* Primary level */
        this.primaryFocusTrap = createFocusTrap(
            [this.$header, this.$primaryPanel],
            {
                onActivate: this.onPrimaryActivateBind,
                onPostActivate: this.onPrimaryPostActivateBind,
                onDeactivate: this.onPrimaryDeactivateBind,
                onPostDeactivate: this.onPrimaryPostDeactivateBind,
                clickOutsideDeactivates: true,
            }
        );

        /* Secondary level */
        this.secondaryFocusTrap = createFocusTrap(
            [this.$header, this.$secondaryPanel],
            {
                onActivate: this.onSecondaryActivateBind,
                onPostActivate: this.onSecondaryPostActivateBind,
                onDeactivate: this.onSecondaryDeactivateBind,
                onPostDeactivate: this.onSecondaryPostDeactivateBind,
                clickOutsideDeactivates: true,
            }
        );
    }

    /******************/
    /* Lifecycle */

    /******************/
    init() {
        this.bindEvents();
    }

    destroy() {
        this.unbindEvents();

        this.primaryFocusTrap?.deactivate?.({
            returnFocus: false,
        });
    }

    /******************/
    /* Events */

    /******************/
    bindEvents() {
        this.$close?.addEventListener("click", this.closeGalleryBind);
        this.$secondaryClose?.addEventListener("click", this.closeSecondaryBind);

        for (const $secondaryButtons of this.$secondaryButtons) {
            $secondaryButtons.addEventListener("click", this.openSecondaryBind);
        }

        this.openEvent = delegateEvent(document.body, "click", "[data-gallery-cta]", this.openGalleryBind);
    }

    unbindEvents() {
        this.$close?.removeEventListener("click", this.closeGalleryBind);
        this.$secondaryClose?.removeEventListener("click", this.closeSecondaryBind);

        for (const $secondaryButtons of this.$secondaryButtons) {
            $secondaryButtons.removeEventListener("click", this.openSecondaryBind);
        }

        this.openEvent?.()
    }

    /******************/
    /* Primary callbacks */

    /******************/
    onPrimaryActivate() {
        this.el.classList.add("is-active");
        this.el.setAttribute("aria-hidden", false);
    }

    onPrimaryPostActivate() {
        this.isPrimaryOpen = true;
        this.$triggerCta?.setAttribute("aria-expanded", true);
    }

    onPrimaryDeactivate() {
        this.el.classList.remove("is-active");
        $html.classList.remove(Gallery.ACTIVE_CLASS);
        this.el.setAttribute("aria-hidden", true);
    }

    onPrimaryPostDeactivate() {
        this.isPrimaryOpen = false;
        this.$triggerCta?.setAttribute("aria-expanded", false);
    }

    /******************/
    /* Secondary callbacks */

    /******************/
    onSecondaryActivate() {
        this.$secondaryPanel.classList.add("is-active");
        this.currentSecondaryFocusTrap.$button.classList.add("is-active");

        this.$secondaryPanel.setAttribute(
            "aria-hidden",
            false
        );
    }

    onSecondaryPostActivate() {
        this.currentSecondaryFocusTrap.$button.setAttribute(
            "aria-expanded",
            true
        );
    }

    onSecondaryDeactivate() {
        this.el.classList.remove('is-secondary');
        this.$secondaryPanel.classList.remove("is-active");
        this.currentSecondaryFocusTrap.$button.classList.remove(
            "is-active"
        );
        this.$secondaryPanel.setAttribute(
            "aria-hidden",
            true
        );
        this.currentSecondaryFocusTrap.$button.setAttribute(
            "aria-expanded",
            false
        );
    }

    onSecondaryPostDeactivate() {
        this.currentSecondaryFocusTrap = null;
    }

    /******************/
    /* Methods */
    /******************/

    openGallery() {
        if(!this.el.classList.contains("is-active")) {
            this.$triggerCta = event.target
            this.openPrimary();
        }
    }

    closeGallery() {
        if (this.el.classList.contains("is-active")) {
            this.closeSecondary();
            this.closePrimary();
        }
    }

    openPrimary() {
        if (this.isPrimaryOpen) return;

        this.$primaryPanel.scrollTo(0,0);
        $html.classList.add(Gallery.ACTIVE_CLASS);

        requestAnimationFrame(() => {
            this.primaryFocusTrap?.activate?.();
        })
    }

    closePrimary() {
        if (!this.isPrimaryOpen) return;
        this.$triggerCta = null;
        this.primaryFocusTrap?.deactivate?.();
    }

    openSecondary(event) {
        const $target = event.currentTarget;

        this.currentSecondaryFocusTrap = {
            focusTrap: this.secondaryFocusTrap,
            $button: $target,
        };

        this.el.classList.add('is-secondary');

        requestAnimationFrame(() => {
            this.currentSecondaryFocusTrap?.focusTrap?.activate?.();
        })

        const indexItem =  this.currentSecondaryFocusTrap.$button.dataset.index
        this.call('setIndex', { index: indexItem }, 'CarouselGallery')
    }

    closeSecondary() {
        this.currentSecondaryFocusTrap?.focusTrap?.deactivate?.();
    }
}
